import { spawn } from 'node:child_process'

import type { RunMode, RunVercelResult, VercelRunner } from './types.js'

/**
 * Pinned Vercel CLI version used for the `npx` fallback. Pinning keeps the
 * provisioning flow reproducible so a future CLI release can't silently change
 * the `integration add` flags or JSON output BetterStart depends on.
 */
export const PINNED_VERCEL_VERSION = '48'

const DEFAULT_CAPTURE_TIMEOUT_MS = 120_000

function pathRunner(): VercelRunner {
  return { bin: 'vercel', prefix: [], source: 'path' }
}

function npxRunner(): VercelRunner {
  return { bin: 'npx', prefix: ['-y', `vercel@${PINNED_VERCEL_VERSION}`], source: 'npx' }
}

/**
 * Detect a usable Vercel runner. Prefers a global `vercel` on PATH (no download,
 * respects the user's installed version); falls back to `npx vercel@<pin>`.
 * npx reachability is probed lazily at first real use so offline degrades to a
 * soft failure rather than blocking here.
 */
export async function resolveVercelRunner(): Promise<VercelRunner> {
  const probe = await runVercel(pathRunner(), ['--version'], {
    cwd: process.cwd(),
    mode: 'capture',
    timeoutMs: 10_000
  })
  return probe.success ? pathRunner() : npxRunner()
}

/**
 * Core subprocess primitive for the Vercel CLI. Modeled on `runQuietCommand`
 * (capture) and `runDrizzlePush` (inherit) in the init command.
 * - `capture`: piped stdio with a timeout; collects stdout/stderr for parsing.
 * - `inherit`: interactive stdio for `vercel login` and marketplace terms so the
 *   user can approve the printed link/code. No output is captured.
 * `env` is merged over `process.env`, which is how `VERCEL_TOKEN` flows through.
 */
export function runVercel(
  runner: VercelRunner,
  args: string[],
  options: { cwd: string; mode: RunMode; timeoutMs?: number; env?: NodeJS.ProcessEnv }
): Promise<RunVercelResult> {
  const timeoutMs = options.timeoutMs ?? DEFAULT_CAPTURE_TIMEOUT_MS
  const argv = [...runner.prefix, ...args]

  return new Promise((resolve) => {
    const child = spawn(runner.bin, argv, {
      cwd: options.cwd,
      stdio: options.mode === 'inherit' ? 'inherit' : 'pipe',
      env: { ...process.env, ...options.env }
    })

    let stdout = ''
    let stderr = ''
    let timedOut = false

    if (options.mode === 'capture') {
      child.stdout?.on('data', (chunk: Buffer) => {
        stdout += chunk.toString()
      })
      child.stderr?.on('data', (chunk: Buffer) => {
        stderr += chunk.toString()
      })
    }

    const timeout = setTimeout(() => {
      timedOut = true
      child.kill('SIGTERM')
    }, timeoutMs)

    child.on('close', (code, signal) => {
      clearTimeout(timeout)

      if (timedOut) {
        resolve({
          success: false,
          code,
          signal,
          stdout,
          stderr,
          timedOut: true,
          errorMessage: `Vercel command timed out after ${Math.floor(timeoutMs / 1000)}s`
        })
        return
      }

      resolve({
        success: code === 0,
        code,
        signal,
        stdout,
        stderr,
        timedOut: false,
        errorMessage:
          code === 0
            ? undefined
            : signal
              ? `Vercel command terminated by ${signal}`
              : `Vercel command exited with code ${code ?? 'unknown'}`
      })
    })

    child.on('error', (err) => {
      clearTimeout(timeout)
      resolve({
        success: false,
        code: null,
        signal: null,
        stdout,
        stderr,
        timedOut: false,
        errorMessage: err.message
      })
    })
  })
}

/**
 * Parse a JSON payload out of mixed Vercel stdout. The CLI can print human
 * banners around `--format json` output, so we try the whole string first, then
 * fall back to the outermost `{...}`/`[...]` span. Any parse miss returns
 * undefined (soft failure) rather than throwing.
 */
export function parseVercelJson<T = unknown>(stdout: string): T | undefined {
  const trimmed = stdout.trim()
  if (!trimmed) return undefined

  try {
    return JSON.parse(trimmed) as T
  } catch {
    // fall through to span extraction
  }

  const span = extractOutermostJson(trimmed)
  if (!span) return undefined

  try {
    return JSON.parse(span) as T
  } catch {
    return undefined
  }
}

/** Slice the span from the first `{`/`[` to the last matching `}`/`]`. */
function extractOutermostJson(text: string): string | undefined {
  const starts = [text.indexOf('{'), text.indexOf('[')].filter((i) => i >= 0)
  if (starts.length === 0) return undefined
  const start = Math.min(...starts)

  const end = Math.max(text.lastIndexOf('}'), text.lastIndexOf(']'))
  if (end <= start) return undefined

  return text.slice(start, end + 1)
}
